/*
 * Copyright 2025 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/mobile-mobile/blob/master/LICENSE.md
 */
package org.mifos.mobile.feature.accounts.accounts

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import mifos_mobile.feature.accounts.generated.resources.Res
import mifos_mobile.feature.accounts.generated.resources.feature_accounts_filter_status
import mifos_mobile.feature.accounts.generated.resources.feature_accounts_filter_type
import mifos_mobile.feature.accounts.generated.resources.feature_loan_account_title
import mifos_mobile.feature.accounts.generated.resources.feature_saving_account_title
import mifos_mobile.feature.accounts.generated.resources.feature_share_account_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.mifos.mobile.core.designsystem.component.BasicDialogState
import org.mifos.mobile.core.designsystem.component.MifosBasicDialog
import org.mifos.mobile.core.designsystem.component.MifosBottomSheet
import org.mifos.mobile.core.designsystem.component.MifosElevatedScaffold
import org.mifos.mobile.core.designsystem.component.rememberMifosPullToRefreshState
import org.mifos.mobile.core.designsystem.theme.DesignToken
import org.mifos.mobile.core.designsystem.theme.MifosMobileTheme
import org.mifos.mobile.core.model.enums.AccountType
import org.mifos.mobile.core.ui.component.FilterTopSection
import org.mifos.mobile.core.ui.component.MifosPoweredCard
import org.mifos.mobile.core.ui.component.MifosProgressIndicator
import org.mifos.mobile.core.ui.utils.DevicePreview
import org.mifos.mobile.core.ui.utils.EventsEffect
import org.mifos.mobile.core.ui.utils.ScreenUiState
import org.mifos.mobile.feature.accounts.component.FilterSection
import org.mifos.mobile.feature.accounts.model.FilterType
import org.mifos.mobile.feature.loanaccount.loanAccount.LoanAccountScreen
import org.mifos.mobile.feature.savingsaccount.savingsAccount.SavingsAccountScreen
import org.mifos.mobile.feature.shareaccount.shareAccount.ShareAccountScreen
import template.core.base.designsystem.theme.KptTheme

/**
 * Composable function that displays the Accounts Screen.
 *
 * @param navigateBack The function to be called when the back button is pressed.
 * @param onAccountClicked The function to be called when an account is clicked.
 * @param viewModel The ViewModel to be used for the screen.
 */
@Composable
internal fun AccountsScreen(
    navigateBack: () -> Unit,
    onAccountClicked: (accountType: String, accountId: Long) -> Unit,
    viewModel: AccountsViewModel = koinViewModel(),
) {
    val state by viewModel.stateFlow.collectAsStateWithLifecycle()

    EventsEffect(viewModel.eventFlow) { event ->
        when (event) {
            is AccountsEvent.AccountClicked -> {
                onAccountClicked(event.accountType, event.accountId)
            }
            AccountsEvent.NavigateBack -> navigateBack.invoke()
        }
    }

    AccountScreenContent(
        state = state,
        onAction = remember(viewModel) {
            { viewModel.trySendAction(it) }
        },
    )

    AccountsDialog(
        state = state,
        onAction = remember(viewModel) {
            { viewModel.trySendAction(it) }
        },
    )
}

/**
 * Composable function that displays the Accounts Dialog.
 *
 * @param state The state of the screen.
 * @param onAction The function to be called when an action is performed.
 * @param modifier Modifier to be applied to the layout.
 */
@Composable
internal fun AccountsDialog(
    state: AccountsState,
    onAction: (AccountsAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    when (state.dialogState) {
        is AccountsState.DialogState.Error -> {
            MifosBasicDialog(
                visibilityState = BasicDialogState.Shown(
                    message = state.dialogState.message,
                ),
                onDismissRequest = { onAction(AccountsAction.DismissDialog) },
            )
        }
        AccountsState.DialogState.Filters -> MifosBottomSheet(
            content = {
                SavingsAccountFilters(
                    state = state,
                    onAction = onAction,
                )
            },
            modifier = modifier,
            onDismiss = { onAction(AccountsAction.DismissDialog) },
        )
        null -> {}
    }
}

/**
 * Composable function that displays the Savings Account Filters Bottom Bar.
 *
 * @param state The state of the screen.
 * @param onAction The function to be called when an action is performed.
 * @param modifier Modifier to be applied to the layout.
 */
@Composable
internal fun SavingsAccountFilters(
    state: AccountsState,
    onAction: (AccountsAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(KptTheme.spacing.md)
            .padding(top = KptTheme.spacing.md),
    ) {
        FilterTopSection(
            isAnyFilterSelected = state.isAnyFilterSelected,
            resetFilters = {
                onAction(AccountsAction.ResetFilters)
            },
            onApplyFilter = {
                onAction(AccountsAction.GetFilterResults)
            },
            dismissDialog = {
                onAction(AccountsAction.DismissDialog)
            },
        )

        Spacer(Modifier.height(DesignToken.spacing.largeIncreased))

        HorizontalDivider(modifier = Modifier.height(DesignToken.strokes.thin))

        FilterSection(
            title = stringResource(Res.string.feature_accounts_filter_type),
            filtersSelected = state.accountTypeFiltersCount ?: 0,
            isExpanded = state.isTypeExpanded,
            onToggle = { onAction(AccountsAction.ToggleTypeExpanded) },
            filters = state.checkboxOptions.filter { it.type == FilterType.ACCOUNT_TYPE },
            onCheckChanged = { label ->
                onAction(AccountsAction.ToggleCheckbox(label, FilterType.ACCOUNT_TYPE))
            },
        )

        FilterSection(
            title = stringResource(Res.string.feature_accounts_filter_status),
            filtersSelected = state.accountStatusFiltersCount ?: 0,
            isExpanded = state.isStatusExpanded,
            onToggle = { onAction(AccountsAction.ToggleStatusExpanded) },
            filters = state.checkboxOptions.filter { it.type == FilterType.ACCOUNT_STATUS },
            onCheckChanged = { label ->
                onAction(AccountsAction.ToggleCheckbox(label, FilterType.ACCOUNT_STATUS))
            },
        )
    }
}

/**
 * Composable function that displays the Account Screen Content.
 *
 * @param state The state of the screen.
 * @param onAction The function to be called when an action is performed.
 */
@Composable
internal fun AccountScreenContent(
    state: AccountsState,
    onAction: (AccountsAction) -> Unit,
) {
    val isRefreshing = state.isRefreshing
    val pullToRefreshState = rememberMifosPullToRefreshState(
        isEnabled = true,
        isRefreshing = isRefreshing,
        onRefresh = {
            onAction(AccountsAction.Refresh)
        },
    )

    MifosElevatedScaffold(
        onNavigateBack = { onAction(AccountsAction.OnNavigateBack) },
        topBarTitle = when (state.accountType) {
            AccountType.SAVINGS -> stringResource(Res.string.feature_saving_account_title)
            AccountType.LOAN -> stringResource(Res.string.feature_loan_account_title)
            AccountType.SHARE -> stringResource(Res.string.feature_share_account_title)
        },
        pullToRefreshState = pullToRefreshState,
        bottomBar = {
            Surface {
                MifosPoweredCard(
                    modifier = Modifier
                        .fillMaxWidth()
                        .navigationBarsPadding(),
                )
            }
        },
    ) {
        when (state.uiState) {
            ScreenUiState.Success -> {
                val typeFilters = state.selectedFilters.filter { it.type == FilterType.ACCOUNT_TYPE }
                val statusFilters = state.selectedFilters.filter { it.type == FilterType.ACCOUNT_STATUS }
                when (state.accountType) {
                    AccountType.SAVINGS -> {
                        SavingsAccountScreen(
                            navigateBack = { onAction(AccountsAction.OnNavigateBack) },
                            refreshSignal = state.refreshSignal,
                            onLoadingCompleted = {
                                onAction(AccountsAction.RefreshCompleted)
                            },
                            onAccountClicked = { accountType, accountId ->
                                onAction(AccountsAction.OnAccountClicked(accountId, accountType))
                            },
                            accountTypeFilters = typeFilters.map { it.statusLabel },
                            accountStatusFilters = statusFilters.map { it.statusLabel },
                            filtersClicked = { onAction(AccountsAction.ToggleFilter) },
                        )
                    }
                    AccountType.LOAN -> {
                        LoanAccountScreen(
                            navigateBack = { onAction(AccountsAction.OnNavigateBack) },
                            refreshSignal = state.refreshSignal,
                            onLoadingCompleted = {
                                onAction(AccountsAction.RefreshCompleted)
                            },
                            onAccountClicked = { accountType, accountId ->
                                onAction(AccountsAction.OnAccountClicked(accountId, accountType))
                            },
                            accountTypeFilters = typeFilters.map { it.statusLabel },
                            accountStatusFilters = statusFilters.map { it.statusLabel },
                            filtersClicked = { onAction(AccountsAction.ToggleFilter) },
                        )
                    }
                    AccountType.SHARE -> {
                        ShareAccountScreen(
                            navigateBack = { onAction(AccountsAction.OnNavigateBack) },
                            refreshSignal = state.refreshSignal,
                            onLoadingCompleted = {
                                onAction(AccountsAction.RefreshCompleted)
                            },
                            onAccountClicked = { accountType, accountId ->
                                onAction(AccountsAction.OnAccountClicked(accountId, accountType))
                            },
                            accountTypeFilters = typeFilters.map { it.statusLabel },
                            accountStatusFilters = statusFilters.map { it.statusLabel },
                            filtersClicked = { onAction(AccountsAction.ToggleFilter) },
                        )
                    }
                }
            }

            ScreenUiState.Loading -> MifosProgressIndicator()

            else -> { }
        }
    }
}

@DevicePreview
@Composable
internal fun ClientAccountsScreenPreview() {
    MifosMobileTheme {
        AccountScreenContent(
            state = AccountsState(dialogState = null),
            onAction = {},
        )
    }
}
